import {Component, Output} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {firstValueFrom} from 'rxjs';
import {CpmCFileApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-file.api-service';
import {FileEntryModelExtension} from '../../api-extensions/models/file-entry.model-extension';
import {ECpmCFileEntryTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/c-common/cpmc-file-entry.api-enums';
import {FileUtils} from '../../utils/file.utils';
import {ReenforcedConfirmModalComponent} from '../../../../sdk/src/modals/reenforced-confirm-modal/reenforced-confirm-modal.component';
import {IBaseComponent} from '../../../../sdk/src/components/ibase/ibase.component';
import {MenuItem} from 'primeng/api';
import {ECpmCRouteTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {
    NewUserFileDirectoryModalComponent,
    NewUserFileDirectoryModalResult
} from '../../modals/file/new-user-file-directory-modal/new-user-file-directory-modal.component';
import {
    FileDetailsModalComponent,
    FileDetailsModalConfig
} from '../../modals/file/file-details-modal/file-details-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {DynamicDialogRef} from 'primeng/dynamicdialog';
import {IBaseCRUModalResultBase} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {
    UploadFileModalComponent,
    UploadFileModalConfig
} from '../../modals/file/upload-file-modal/upload-file-modal.component';
import {
    RenameFileModalComponent,
    RenameFileModalResult
} from '../../modals/file/rename-file-modal/rename-file-modal.component';
import {MoveFileModalComponent, MoveFileModalResult} from '../../modals/file/move-file-modal/move-file-modal.component';
import {UserDataCellDisplayComponent} from '../../data-cell-display/user-data-cell-display/user-data-cell-display.component';

/**
 * Organization CRUD table component
 */
@Component({
    selector: 'app-user-file-crud',
    templateUrl: './user-file-crud.component.html',
    styleUrl: '../../../../sdk/src/components/generic-table/generic-table.component.scss',
    imports: [PrimeNgImportsModule, SdkImportsModule, UserDataCellDisplayComponent]
})
export class UserFileCRUDComponent extends IBaseComponent {
    protected _authorizationUpload = false;
    protected _authorizationCreateDir = false;
    protected _authorizationCreateFile = false;
    protected _authorizationDelete = false;
    protected _authorizationDownload = false;
    protected _authorizationRename = false;
    protected _authorizationMove = false;
    protected _items: FileEntryModelExtension[] = [];
    protected _currentPathRoot?: FileEntryModelExtension;
    protected _currentPathRootResolved = '/';
    protected _searchValue?: string;
    protected _contextMenuSelection: unknown;
    protected _contextMenu: MenuItem[] = [{separator: true}];
    protected _moveUpEntry: FileEntryModelExtension = {
        uuid: '',
        created_at: new Date(),
        created_by_uuid: undefined,
        parent_uuid: '',
        type: ECpmCFileEntryTypeApiEnum.USER,
        is_dir: true,
        parent_path: '/',
        name: 'Go back',
        size: 0,
        exist: true,
        expire_at: undefined,
        parent: undefined,
        childrens: undefined,
        s3_bucket: undefined
    };

    // ===============================================================
    // ===============================================================

    @Output()
    public get currentPathRootResolved(): string {
        return this._currentPathRootResolved;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        await this.globalsService.sessionService.getRouteContextIsReadyPromise();
        this._authorizationUpload = this.globalsService.sessionService.isAuthorizedForRouteResource(
            CpmCFileApiService.endpointCreateFileEntryForUpload(this.FAKE_UUID),
            ECpmCRouteTypeApiEnum.CREATE
        );

        this._authorizationCreateFile = this.globalsService.sessionService.isAuthorizedForRouteResource(
            CpmCFileApiService.endpointDeleteUserFile(this.FAKE_UUID, this.FAKE_UUID),
            ECpmCRouteTypeApiEnum.CREATE
        );

        this._authorizationCreateDir = this.globalsService.sessionService.isAuthorizedForRouteResource(
            CpmCFileApiService.endpointCreateUserDir(this.FAKE_UUID),
            ECpmCRouteTypeApiEnum.CREATE
        );

        this._authorizationDelete = this.globalsService.sessionService.isAuthorizedForRouteResource(
            CpmCFileApiService.endpointDeleteUserFile(this.FAKE_UUID, this.FAKE_UUID),
            ECpmCRouteTypeApiEnum.DELETE
        );

        this._authorizationDownload = this.globalsService.sessionService.isAuthorizedForRouteResource(
            CpmCFileApiService.endpointDownload(this.FAKE_UUID, this.FAKE_UUID, false),
            ECpmCRouteTypeApiEnum.READ
        );

        this._authorizationRename = this.globalsService.sessionService.isAuthorizedForRouteResource(
            CpmCFileApiService.endpointUpdateUserFile(this.FAKE_UUID, this.FAKE_UUID),
            ECpmCRouteTypeApiEnum.UPDATE
        );

        this._authorizationMove = this.globalsService.sessionService.isAuthorizedForRouteResource(
            CpmCFileApiService.endpointUpdateUserFile(this.FAKE_UUID, this.FAKE_UUID),
            ECpmCRouteTypeApiEnum.UPDATE
        );
        await this.restoreFromUrl();
        await this.refresh();
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * Refresh
     */
    public async refresh(): Promise<void> {
        this.showLoading = true;
        this.showError = false;

        try {
            await this.parallelSequencedAsyncCritical([
                {
                    m: 'Loading current path...',
                    f: async (): Promise<void> => {
                        if (!this._currentPathRoot) {
                            this._currentPathRootResolved = '/';
                            return;
                        }

                        this._currentPathRootResolved = await firstValueFrom(
                            CpmCFileApiService.getResolvedUserFilePath(this._currentPathRoot.uuid)
                        );
                    }
                },
                {
                    m: 'Loading files...',
                    f: async (): Promise<void> => {
                        // TODO: Add project filtering when backend supports project_uuid in file entries
                        // Current limitation: CpmCCustomerFileEntryApiModel does not have a project_uuid field
                        this._items = (
                            await firstValueFrom(
                                CpmCFileApiService.getUserFileList(
                                    this._currentPathRoot
                                        ? {parent_uuid: this._currentPathRoot?.uuid}
                                        : {parent_path: ''}
                                )
                            )
                        ).data;

                        this._items.forEach((x) => {
                            x._formated_size = FileUtils.formatFileSize(x.size);
                        });
                        this._items.sort((a, b) => {
                            if (a.is_dir && !b.is_dir) return -1;
                            else if (!a.is_dir && b.is_dir) return 1;

                            return -1;
                        });
                    }
                }
            ]);

            if (this._currentPathRootResolved != '/') {
                this._items = [this._moveUpEntry, ...this._items];
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                detail: 'Error while refreshing: ' + (err?.error?.message ?? err.toString())
            });

            // Log error in the console for debugging, do not remove
            console.log(err);

            this.showError = true;
        }

        this.showLoading = false;
    }

    // ===============================================================
    // ===============================================================

    protected onView(row: FileEntryModelExtension) {
        this.handleDialogRef<FileDetailsModalComponent, FileDetailsModalConfig>(
            this.globalsService.dialogService.open(
                FileDetailsModalComponent,
                FileDetailsModalComponent.getDialogServiceParameters({
                    dialogOpenMode: EDialogOpenMode.EDIT,
                    extended: {
                        fileEntry: row
                    }
                })
            )
        );
    }
    /**
     * On create directory
     */
    protected onCreateDirectory() {
        this.handleDialogRef<NewUserFileDirectoryModalComponent, NewUserFileDirectoryModalResult>(
            this.globalsService.dialogService.open(
                NewUserFileDirectoryModalComponent,
                NewUserFileDirectoryModalComponent.getDialogServiceParameters({
                    dialogOpenMode: EDialogOpenMode.NEW,
                    extended: {
                        atResolvedPath: this._currentPathRootResolved
                    }
                })
            )
        );
    }
    /**
     * On upload file
     */
    protected onUploadFile() {
        this.handleDialogRef<UploadFileModalComponent, UploadFileModalConfig>(
            this.globalsService.dialogService.open(
                UploadFileModalComponent,
                UploadFileModalComponent.getDialogServiceParameters({
                    dialogOpenMode: EDialogOpenMode.NEW,
                    extended: {
                        postUploadAction: async (fileEntries: FileEntryModelExtension[]) => {
                            for (const fileEntry of fileEntries) {
                                await firstValueFrom(
                                    CpmCFileApiService.postAddUserFile({
                                        file_entry_uuid: fileEntry.uuid,
                                        at_resolved_path: this._currentPathRootResolved
                                    })
                                );
                            }
                        }
                    }
                })
            )
        );
    }
    /**
     * On rename file
     * @param row File entry to rename
     */
    protected onRename(row: FileEntryModelExtension) {
        this.handleDialogRef<RenameFileModalComponent, RenameFileModalResult>(
            this.globalsService.dialogService.open(
                RenameFileModalComponent,
                RenameFileModalComponent.getDialogServiceParameters({
                    dialogOpenMode: EDialogOpenMode.EDIT,
                    extended: {
                        fileEntry: row,
                        currentFileName: row.name
                    }
                })
            )
        );
    }
    /**
     * On move file/folder
     * @param row File entry to move
     */
    protected onMove(row: FileEntryModelExtension) {
        this.handleDialogRef<MoveFileModalComponent, MoveFileModalResult>(
            this.globalsService.dialogService.open(
                MoveFileModalComponent,
                MoveFileModalComponent.getDialogServiceParameters({
                    dialogOpenMode: EDialogOpenMode.EDIT,
                    extended: {
                        fileEntry: row,
                        currentFolderUUID: this._currentPathRoot?.uuid
                    }
                })
            )
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * On t_Model delete
     * @param itemToDelete t_Model to delete
     */
    public async onDelete(itemToDelete: FileEntryModelExtension) {
        if (itemToDelete.is_dir) {
            this.globalsService.dialogService
                .open(
                    ReenforcedConfirmModalComponent,
                    ReenforcedConfirmModalComponent.getDialogServiceParameters({
                        isCreate: false,
                        message: `Are you sure that you want to delete directory '${itemToDelete.name}'? all the directories and files inside will be as well`,
                        confirmation: itemToDelete.name
                    })
                )
                .onClose.subscribe((res: boolean) => {
                    if (res) {
                        this.callDelete(itemToDelete, true);
                    }
                });
        } else {
            this.globalsService.confirmationService.confirm({
                key: 'deletion-confirm',
                header: 'Deletion confirmation',
                message: `Are you sure that you want to delete file '${itemToDelete.name}'?`,
                accept: () => {
                    this.callDelete(itemToDelete, true);
                }
            });
        }
    }
    /**
     * trigger delete service process
     * @param itemToDelete t_Model to delete
     * @param autoRefresh Automatically refresh after deletion
     * @returns true when deletion succed or false when failed
     */
    private async callDelete(itemToDelete: FileEntryModelExtension, autoRefresh = true) {
        this.showLoading = true;
        this.showError = false;

        try {
            await firstValueFrom(CpmCFileApiService.deleteUserFile(itemToDelete.uuid));

            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'success',
                summary: 'Deleting ' + (itemToDelete.is_dir ? 'directory' : 'file'),
                detail: `Successfully deleted the ${itemToDelete.is_dir ? 'directory' : 'file'} "${itemToDelete.name}".`
            });

            if (autoRefresh) await this.refresh();

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            this.showError = true;
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                summary: `Deleting ${itemToDelete.is_dir ? 'directory' : 'file'} "${itemToDelete.name}"  error`,
                detail: err?.error?.message ?? 'Unknown'
            });
        }

        this.showLoading = false;
        return !this.showError;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Handle t_ModalType result
     * @param dialogRef Dialog reference from t_ModalType
     */
    private handleDialogRef<t_ModalType, t_ModalExtendedResult>(dialogRef: DynamicDialogRef<t_ModalType>) {
        dialogRef.onClose.subscribe(async (result: IBaseCRUModalResultBase<t_ModalExtendedResult>) => {
            if (result == undefined) {
                return;
            }

            this.showLoading = true;

            if (
                result.dialogOpenMode === EDialogOpenMode.NEW ||
                result.dialogOpenMode == EDialogOpenMode.EDIT ||
                result.dialogOpenMode == EDialogOpenMode.CLONE
            ) {
                await this.refresh();
            }

            this.showLoading = false;
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * When the context menu is shown on a node
     * @param row Context row
     */
    public onContextMenuSelect(row: FileEntryModelExtension) {
        this._contextMenu = [];

        if (row == this._moveUpEntry) {
            this._contextMenu.push({
                label: 'No action available...',
                disabled: true
            });

            return;
        }

        if (row.is_dir && row != this._moveUpEntry) {
            this._contextMenu.push(
                {
                    label: 'Open',
                    icon: 'pi pi-folder-open',
                    command: () => {
                        this.navigateTo(row);
                    }
                },
                {
                    label: 'Details',
                    icon: 'pi pi-eye',
                    command: () => {
                        this.onView(row);
                    }
                },
                {
                    label: 'Rename',
                    icon: 'pi pi-pencil',
                    disabled: !this._authorizationRename,
                    command: () => {
                        this.onRename(row);
                    }
                },
                {
                    separator: true
                }
            );
        } else if (!row.is_dir && row != this._moveUpEntry) {
            this._contextMenu.push(
                {
                    separator: true
                },
                {
                    label: 'Download',
                    icon: 'pi pi-download',
                    disabled: !this._authorizationDownload,
                    command: () => {
                        this.downloadFile(row);
                    }
                },
                {
                    label: 'Details',
                    icon: 'pi pi-eye',
                    command: () => {
                        this.onView(row);
                    }
                },
                {
                    label: 'Rename',
                    icon: 'pi pi-pencil',
                    disabled: !this._authorizationRename,
                    command: () => {
                        this.onRename(row);
                    }
                },
                {
                    separator: true
                }
            );
        }

        this._contextMenu.push(
            {
                label: 'Details',
                icon: 'pi pi-eye',
                command: () => {
                    this.onView(row);
                }
            },
            {
                label: 'Move to',
                icon: 'pi pi-arrow-right',
                disabled: !this._authorizationMove,
                command: () => {
                    this.onMove(row);
                }
            },
            {
                label: 'Delete',
                icon: 'pi pi-trash',
                disabled: !this._authorizationDelete,
                command: () => {
                    this.onDelete(row);
                }
            }
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Navigate to folder
     * @param fileEntry folder file entry
     */
    protected navigateTo(fileEntry: FileEntryModelExtension) {
        if (!fileEntry.is_dir) return;

        this._currentPathRoot = fileEntry;
        this.updateUrl();
        this.refresh();
    }
    /**
     * Navigate up (to parent)
     */
    protected async navigateUp() {
        if (!this._currentPathRoot) return;

        this.showLoading = true;

        await this.asyncCritical(async () => {
            if (this._currentPathRoot?.parent_uuid)
                this._currentPathRoot = await firstValueFrom(
                    CpmCFileApiService.getFileEntryByUUID(this._currentPathRoot.parent_uuid)
                );
            else this._currentPathRoot = undefined;

            this.updateUrl();
            this.refresh();
        });
    }
    /**
     * Download a file
     * @param fileEntry file to download
     */
    protected async downloadFile(fileEntry: FileEntryModelExtension) {
        if (fileEntry.is_dir) return;

        await this.asyncCritical(async () => {
            const publicUrl = await firstValueFrom(CpmCFileApiService.getDownloadByUUID(fileEntry.uuid));
            await FileUtils.downloadFile(publicUrl);
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Restore current path root from URL
     */
    protected async restoreFromUrl() {
        try {
            const currentPathRootUUID =
                this.globalsService.router.routerState.snapshot.root.queryParams['currentPathUUID'] ?? undefined;
            if (!currentPathRootUUID) return;

            await this.asyncCritical(async () => {
                this._currentPathRoot = await firstValueFrom(
                    CpmCFileApiService.getFileEntryByUUID(currentPathRootUUID)
                );
            });
        } catch {
            /* do nothing */
        }
    }
    /**
     * Update the URL to reflect current path root
     */
    protected updateUrl() {
        const currentQs = {
            ...this.globalsService.router.routerState.snapshot.root.queryParams
        };
        const excludeKeys = ['currentPathUUID'];
        for (const key of excludeKeys) {
            delete currentQs[key];
        }

        const newQs = this._currentPathRoot ? {currentPathUUID: this._currentPathRoot.uuid} : {};
        const merged = {...currentQs, ...newQs};

        this.globalsService.router.navigate([], {
            queryParams: merged,
            replaceUrl: true
        });
    }
}
